// The physics behind the background, kept pure so it can be unit tested.

// Thin gravitational lens: a star at true distance `beta` from the lens is
// seen at theta = (beta + sqrt(beta² + 4·θE²)) / 2, where θE is the Einstein
// radius. Stars right behind the lens get smeared onto a ring of radius θE.
export function lensRadius(beta, thetaE) {
  return (beta + Math.sqrt(beta * beta + 4 * thetaE * thetaE)) / 2;
}

// How far a star at (dx, dy) from the lens appears to move.
export function lensOffset(dx, dy, thetaE) {
  if (thetaE <= 0) return [0, 0];
  const r = Math.hypot(dx, dy);
  if (r < 1e-6) return [thetaE, 0];
  const k = lensRadius(r, thetaE) / r - 1;
  return [dx * k, dy * k];
}

// A gravitational-wave pulse leaving `w` at speed `w.speed`. Returns the
// strain at `dist` from the source `age` seconds after emission: a carrier
// under a Gaussian envelope that fades with age and spreads with distance.
export function waveStrain(dist, age, w) {
  const s = dist - w.speed * age;
  const env = Math.exp(-(s * s) / (2 * w.width * w.width));
  if (env < 1e-3) return 0;
  const fade = Math.exp(-age / w.life) / Math.sqrt(1 + dist / 240);
  return w.amp * env * fade * Math.sin((2 * Math.PI * s) / w.wavelength);
}

// Gravitational waves are transverse and quadrupolar: they stretch space one
// way while squeezing it the other, not push it outward like a water ripple.
// The star moves mostly sideways, with a cos 2φ ("plus" polarisation) pattern.
export function waveOffset(dx, dy, age, w) {
  const r = Math.hypot(dx, dy);
  if (r < 1e-6) return [0, 0];
  const h = waveStrain(r, age, w);
  if (h === 0) return [0, 0];
  const ux = dx / r;
  const uy = dy / r;
  const quad = Math.cos(2 * Math.atan2(dy, dx));
  return [h * (0.35 * ux - uy * quad), h * (0.35 * uy + ux * quad)];
}

// True once a pulse has travelled past everything on screen and faded.
export function waveDone(age, w, maxDist) {
  return w.speed * age - 4 * w.width > maxDist || Math.exp(-age / w.life) < 0.02;
}

// Angular speed (rad/s) of the three-body orbit. `squeeze` in [0, 1] shrinks
// the orbit for the merger, and tighter orbits spin faster (Kepler: ω ∝ r^-1.5).
export function orbitSpeed(period, squeeze = 0) {
  return (2 * Math.PI) / (period * Math.pow(Math.max(1 - squeeze, 0.08), 1.5));
}

// Three bodies on a shared, slightly tilted circular orbit, 120° apart.
export function orbitPositions(phase, { cx, cy, radius, squeeze = 0 }) {
  const r = radius * (1 - squeeze);
  return [0, 1, 2].map((i) => {
    const a = phase + (i * 2 * Math.PI) / 3;
    return [cx + r * Math.cos(a), cy + r * Math.sin(a) * 0.55];
  });
}
